
import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { ChevronDown, Camera } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface NavbarDropdownsProps {
  isHomePage: boolean;
}

export const NavbarDropdowns: React.FC<NavbarDropdownsProps> = ({ isHomePage }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const servicesDropdownItems = [
    { name: 'Services', path: '/services' },
    { name: 'Residential Roofing', path: '/residential-roofing' },
    { name: 'Commercial Roofing', path: '/commercial-roofing' },
    { name: 'Storm Damage', path: '/storm-damage' },
    { name: 'Church Steeple Repair', path: '/specialty-services/church-steeple-repair' },
    { name: 'Roof Coatings', path: '/specialty-services/roof-coating' },
    { name: 'Brands', path: '/brands' },
    { name: 'Design Your Roof', path: '/design-your-roof' },
    { name: 'Roof Life Expectancy Report', path: '/roof-life-expectancy-report' },
    { name: 'Blog', path: '/blog' },
    { name: 'About', path: '/about' },
    { name: 'Locations', path: '/locations' },
    { name: 'Resources', path: '/resources' },
    { name: 'FAQ', path: '/frequently-asked-questions' },
    { name: 'Careers', path: '/careers' },
    { name: 'Book Inspection', path: '/book-inspection' },
    { name: 'Apply for Financing', path: '/financing' }
  ];

  const getButtonStyles = (isActive = false) => `
    px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 hover:scale-105 flex items-center space-x-1 border-2 border-gray-600 shadow-lg shadow-gray-400/50
    ${isHomePage
      ? 'text-white/90 hover:text-white hover:bg-white/10 hover:shadow-gray-300/60' 
      : 'text-gray-700 hover:text-brand-trophy-gold hover:bg-yellow-50 hover:shadow-gray-400/60'
    }
    ${isHomePage ? '[text-shadow:_1px_1px_0_rgb(75_85_99),_-1px_-1px_0_rgb(75_85_99),_1px_-1px_0_rgb(75_85_99),_-1px_1px_0_rgb(75_85_99)]' : ''}
    ${isActive ? 
      (isHomePage ? 'bg-white/20 text-white shadow-gray-300/70' : 'bg-yellow-100 text-brand-trophy-gold shadow-gray-400/70') 
      : ''
    }
  `;

  const getGalleryButtonStyles = (isActive = false) => `
    px-5 py-3 rounded-xl text-sm font-bold transition-all duration-300 hover:scale-105 flex items-center space-x-2 border-2 border-gray-600 shadow-lg shadow-gray-400/50
    ${isHomePage
      ? 'text-white bg-white/5 hover:bg-white/15 hover:shadow-gray-300/60' 
      : 'text-brand-charcoal-gray bg-gray-50/50 hover:bg-brand-charcoal-gray/5 hover:shadow-gray-400/60'
    }
    ${isHomePage ? '[text-shadow:_1px_1px_0_rgb(75_85_99),_-1px_-1px_0_rgb(75_85_99),_1px_-1px_0_rgb(75_85_99),_-1px_1px_0_rgb(75_85_99)]' : ''}
    ${isActive ? 
      (isHomePage ? 'bg-white/20 shadow-gray-300/70' : 'bg-brand-charcoal-gray/10 shadow-gray-400/70') 
      : ''
    }
  `;

  return (
    <div className="hidden lg:flex items-center space-x-4 ml-8">{/* Added ml-8 for proper spacing from logo */}
      {/* Services & Resources Dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className={getButtonStyles()}>
            <span>Services & Resources</span>
            <ChevronDown className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-48 bg-white border border-gray-200 shadow-lg rounded-lg">
          {servicesDropdownItems.map((item) => (
            <DropdownMenuItem key={item.name} asChild>
              <Link 
                to={item.path}
                className="w-full px-3 py-2 text-sm text-gray-700 hover:bg-yellow-50 hover:text-brand-trophy-gold transition-colors duration-200"
              >
                {item.name}
              </Link>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Gallery - standalone with camera icon */}
      <Button
        onClick={() => navigate('/gallery/')}
        variant="ghost"
        className={getGalleryButtonStyles(location.pathname === '/gallery/')}
      >
        <Camera className="h-4 w-4" />
        <span>Gallery</span>
      </Button>

    </div>
  );
};
